import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { useTRPC } from "../trpc/client";
import { useAvailableMerchants } from "./useAvailableMerchants";

export type OrgOption = {
  id: string;
  displayName: string;
  roles: string[];
  merchantCount: number;
};

/**
 * Every context the user can reach, shaped for the selector card and popup so
 * neither component does arithmetic. Both queries are already in flight from
 * `AccessGate` with the same keys and `staleTime`, so this is a cache read.
 */
export function usePortalContexts() {
  const trpc = useTRPC();
  const { availableMerchants } = useAvailableMerchants();
  const { data: reachableOrgs } = useQuery({
    ...trpc.organizations.list.queryOptions(undefined),
    staleTime: 60_000,
  });

  // An org grant expands every one of its merchants into the feed (A10), so
  // counting the feed is exact for an org the user holds. It is exact for an
  // admin too, whose feed is every merchant — and those are the only two ways
  // a row gets here.
  const orgs = useMemo<OrgOption[]>(
    () =>
      (reachableOrgs ?? []).map((org) => ({
        ...org,
        merchantCount: availableMerchants.filter((m) => m.orgId === org.id)
          .length,
      })),
    [reachableOrgs, availableMerchants]
  );

  return {
    orgs,
    merchants: availableMerchants,
    canSwitch: orgs.length + availableMerchants.length > 1,
  };
}
